import { Badge, Text } from '@repletoryreach/sdk-ui';
import type { logs } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../components/CopyableId';
import { TwoLineCell } from '../../components/TwoLineCell';
import { formatDateTime, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import type { Translate } from '../../i18n/I18nProvider';

/**
 * Serilog writes the level with a capital first letter (`Information`,
 * `Warning`), and the query is an exact string comparison in Postgres, so the
 * console has to send the stored spelling. Sending `info` matched nothing and
 * looked to an operator like "there are no errors".
 */
export const LEVELS: { value: string; labelKey: string }[] = [
  { value: 'Verbose', labelKey: 'logs.level.verbose' },
  { value: 'Debug', labelKey: 'logs.level.debug' },
  { value: 'Information', labelKey: 'logs.level.information' },
  { value: 'Warning', labelKey: 'logs.level.warning' },
  { value: 'Error', labelKey: 'logs.level.error' },
  { value: 'Fatal', labelKey: 'logs.level.fatal' }
];

export const FAMILIES: { value: logs.LogFamily; labelKey: string }[] = [
  { value: 'api', labelKey: 'logs.family.api' },
  { value: 'query', labelKey: 'logs.family.query' },
  { value: 'app', labelKey: 'logs.family.app' },
  { value: 'lifecycle', labelKey: 'logs.family.lifecycle' },
  { value: 'frontend', labelKey: 'logs.family.frontend' }
];

/** Palette names, so the badge resolves them through the theme on both grounds. */
export function levelColor(level: unknown): string {
  const l = String(level ?? '').toLowerCase();
  if (l === 'error' || l === 'fatal' || l === 'critical') return 'red';
  if (l === 'warning' || l === 'warn') return 'orange';
  if (l === 'information' || l === 'info') return 'blue';
  return 'gray';
}

/** Title-cased level for display, so `Information` and `info` read the same. */
export function levelLabel(level: unknown): string {
  const raw = String(level ?? '').trim();
  if (!raw) return '–';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

export function statusColor(status: unknown): string {
  const s = Number(status ?? 0);
  if (s >= 500) return 'red';
  if (s >= 400) return 'orange';
  if (s >= 300) return 'gray';
  if (s >= 200) return 'green';
  return 'gray';
}

export function primarySummary(t: Translate, family: logs.LogFamily, r: logs.LogRow): string {
  switch (family) {
    case 'api':       return `${r.method ?? ''} ${r.path ?? ''} → ${r.status_code ?? '–'} (${Math.round(Number(r.duration_ms ?? 0))}ms)`;
    case 'query':     return String(r.sql_redacted ?? t('logs.summary.noSql'));
    case 'app':       return String(r.message ?? t('logs.summary.noMessage'));
    case 'lifecycle': return `${r.event_code ?? ''}: ${r.detail ?? ''}`;
    case 'frontend':  return `${r.kind ?? ''}: ${r.message ?? ''}`;
    default:          return '';
  }
}

/**
 * One column set for both `/logs` and `/logs/query`. The two pages each had
 * their own copy and the copies had already drifted, so a status code was a
 * coloured badge on one route and a bare number on the other.
 *
 * `correlation_id` earns its place: it is the value support asks a customer to
 * quote, and the one field that follows a single request across identity,
 * gateway and channel-wa. It is shown through `CopyableId` for that reason,
 * not as a label.
 */
export function buildColumns(t: Translate, family: logs.LogFamily) {
  // The qualifier a person needs to read a row sits on the row rather than in
  // a column of its own: the exact moment under the relative one, the
  // environment under the service. Two fewer columns, no less information.
  const base = [
    {
      accessor: 'occurred_at',
      title: t('logs.columns.when'),
      render: (r: Indexed<logs.LogRow>) => (
        <time dateTime={r.occurred_at} title={formatDateTime(r.occurred_at)}>
          <TwoLineCell primary={formatRelative(r.occurred_at)} secondary={formatDateTime(r.occurred_at)} />
        </time>
      )
    },
    {
      accessor: 'service_name',
      title: t('logs.columns.service'),
      render: (r: Indexed<logs.LogRow>) => (
        <TwoLineCell
          primary={<code>{String(r.service_name ?? t('common.none'))}</code>}
          secondary={r.environment ? String(r.environment) : undefined}
        />
      )
    },
    {
      accessor: 'level',
      title: t('logs.columns.level'),
      render: (r: Indexed<logs.LogRow>) => (
        <Badge size="xs" color={levelColor(r.level)} variant="light">{levelLabel(r.level)}</Badge>
      )
    }
  ];

  const correlation = {
    accessor: 'correlation_id',
    title: t('logs.columns.correlation'),
    render: (r: Indexed<logs.LogRow>) =>
      r.correlation_id
        ? <CopyableId value={String(r.correlation_id)} label={t('logs.copyCorrelation')} />
        : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
  };

  switch (family) {
    case 'api':
      return [
        ...base,
        { accessor: 'method', title: t('logs.columns.method') },
        { accessor: 'path', title: t('logs.columns.path') },
        {
          accessor: 'status_code',
          title: t('common.status'),
          render: (r: Indexed<logs.LogRow>) => (
            <Badge size="xs" color={statusColor(r.status_code)} variant="light">
              {String(r.status_code ?? '–')}
            </Badge>
          )
        },
        {
          accessor: 'duration_ms',
          title: t('logs.columns.ms'),
          textAlign: 'right' as const,
          render: (r: Indexed<logs.LogRow>) => Math.round(Number(r.duration_ms ?? 0))
        },
        correlation
      ];
    case 'query':
      return [
        ...base,
        {
          accessor: 'sql_redacted',
          title: t('logs.columns.sql'),
          render: (r: Indexed<logs.LogRow>) => <code style={{ fontSize: 11 }}>{String(r.sql_redacted ?? '')}</code>
        },
        {
          accessor: 'duration_ms',
          title: t('logs.columns.ms'),
          textAlign: 'right' as const,
          render: (r: Indexed<logs.LogRow>) => Math.round(Number(r.duration_ms ?? 0))
        },
        { accessor: 'row_count', title: t('logs.columns.rows'), textAlign: 'right' as const },
        {
          accessor: 'failed',
          title: t('logs.columns.outcome'),
          render: (r: Indexed<logs.LogRow>) => (
            <Badge size="xs" color={r.failed ? 'red' : 'green'} variant="light">
              {r.failed ? t('status.failed') : t('status.ok')}
            </Badge>
          )
        },
        correlation
      ];
    case 'app':
      return [
        ...base,
        { accessor: 'message', title: t('logs.columns.message') },
        // A warning that carries a stack trace and one that does not look
        // identical in a table of messages, and the trace is the whole reason
        // to open the row. The badge says which rows are worth opening; the
        // trace itself is a tab in the drawer.
        {
          accessor: 'exception',
          title: t('logs.columns.exception'),
          render: (r: Indexed<logs.LogRow>) =>
            r.exception
              ? <Badge size="xs" color="red" variant="light">{t('logs.hasException')}</Badge>
              : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
        },
        correlation
      ];
    case 'lifecycle':
      return [
        ...base,
        { accessor: 'event_code', title: t('logs.columns.event') },
        { accessor: 'detail', title: t('logs.columns.detail') },
        correlation
      ];
    case 'frontend':
      return [
        ...base,
        { accessor: 'kind', title: t('logs.columns.kind') },
        { accessor: 'message', title: t('logs.columns.message') },
        correlation
      ];
    default:
      return base;
  }
}
